// PMS coverage — what this practice's portal runbook lets Atlas do (brief 15, item 3).
//
// Every operation the contract names, with its state on the version the queue works
// from, grouped by the Atlas feature it enables; a banner naming the features that
// are unavailable and exactly which operations would enable them. Registry page
// (`pms-coverage`, feature `submit_queue`), so a clearinghouse practice never sees it;
// a queue practice with no submit portal gets the "nothing to show" card instead.
//
// Read-only. Nothing here is PHI: operation keys, feature keys and counts.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchCoverage, fetchSignInState } from "./api";
import { relativeTime } from "./ageing";
import PortalSessionPanel from "./PortalSessionPanel";
import AuthenticatorCard from "./AuthenticatorCard";
import "./Queue.css";

export const STATE_LABEL = {
  covered: "Covered",
  partial: "Partial",
  missing: "Missing",
  not_applicable: "Not applicable",
};

const STATE_CLASS = {
  covered: "sq-status-submitted",
  partial: "sq-cov-partial",
  missing: "sq-status-failed",
  not_applicable: "sq-status-removed",
};

const FEATURE_STATUS_LABEL = {
  complete: "Available",
  partial: "Partial",
  missing: "Unavailable",
};

export function StateBadge({ state }) {
  return (
    <span
      className={`sq-status ${STATE_CLASS[state] || ""}`}
      data-state={state}
    >
      {STATE_LABEL[state] || state}
    </span>
  );
}

// The banner's sentence for one unavailable feature: what it is, what would enable it.
export function unavailableLine(entry) {
  const needs = entry.needs.length
    ? entry.needs.join(", ")
    : "nothing recorded yet";
  return `${entry.label}: needs ${needs}`;
}

export default function CoveragePage({
  selectedPractice,
  isAdmin = false,
  isSuperAdmin = false,
  readOnly = false,
  currentUserId = null,
}) {
  const practiceId = selectedPractice?.id;
  const [coverage, setCoverage] = useState(null);
  // The sign-in state rides along for the portal-session panel's Retry; a failure
  // to read it is not the page's error — the matrix is still current.
  const [signIn, setSignIn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const ticket = useRef(0);

  const load = useCallback(async () => {
    if (!practiceId) return;
    const mine = (ticket.current += 1);
    setLoading(true);
    setError(null);
    try {
      const body = await fetchCoverage(practiceId);
      if (mine !== ticket.current) return;
      setCoverage(body);
    } catch (err) {
      if (mine !== ticket.current) return;
      setError(err.message);
      setCoverage(null);
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
  }, [practiceId]);

  const loadSignIn = useCallback(async () => {
    if (!practiceId) return;
    try {
      setSignIn(await fetchSignInState(practiceId));
    } catch (err) {
      setSignIn(null);
    }
  }, [practiceId]);

  useEffect(() => {
    setCoverage(null);
    setSignIn(null);
    load();
    loadSignIn();
  }, [load, loadSignIn]);

  if (!practiceId) {
    return (
      <div className="sq-page">
        <p className="sq-empty">Select a practice to see its PMS coverage.</p>
      </div>
    );
  }

  const byKey = coverage
    ? Object.fromEntries(coverage.operations.map((o) => [o.key, o]))
    : {};
  const unavailable = coverage ? coverage.unavailable : [];

  return (
    <div className="sq-page">
      <div className="sq-header">
        <div>
          <h1>PMS coverage</h1>
          <p className="sq-sub">
            What Atlas can do against {selectedPractice.name}’s practice
            management system, operation by operation, read off the portal
            runbook the submit queue works from.
          </p>
        </div>
        <button
          className="btn btn-secondary btn-sm"
          onClick={load}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="sq-message error" role="alert">
          <span>{error}</span>
        </div>
      )}

      {loading && !coverage && <p className="sq-empty">Loading coverage…</p>}

      {coverage && !coverage.applicable && (
        <div className="sq-run-banner grey" role="status">
          <span>
            Nothing to show:{" "}
            {coverage.reason || "this practice has no submit portal."}
          </span>
        </div>
      )}

      {/* Portal sessions (docs/portal-sessions.md): admin and above. Rendered whatever
          the coverage says — a practice with a runbook but no submit portal can still
          need a sign-in — and the panel itself says when there is no portal. */}
      {isAdmin && (
        <PortalSessionPanel
          key={practiceId}
          practiceId={practiceId}
          isSuperAdmin={isSuperAdmin}
          readOnly={readOnly}
          signIn={signIn}
          onRetried={loadSignIn}
          currentUserId={currentUserId}
        />
      )}

      {/* Portal authenticator codes (docs/portal-authenticators.md): everyone with
          access to the practice can get a code; a super_admin enrols and revokes.
          Read-only (client) users see nothing: the server refuses them too. Shown
          where the session panel is, or where the coverage says there is a portal. */}
      {(isAdmin || coverage?.applicable) && (
        <AuthenticatorCard
          key={`auth-${practiceId}`}
          practiceId={practiceId}
          isSuperAdmin={isSuperAdmin}
          readOnly={readOnly}
        />
      )}

      {coverage && coverage.applicable && (
        <>
          <div className="sq-run-banner grey">
            <span>
              Portal <strong>{coverage.portalLabel || coverage.portal}</strong>
              {coverage.runbook ? (
                <>
                  {" "}
                  · runbook version {coverage.runbook.version}
                  {coverage.runbook.summary?.schema
                    ? ` (${coverage.runbook.summary.schema})`
                    : ""}
                  {coverage.runbook.approvedBy
                    ? `, approved by ${coverage.runbook.approvedBy}`
                    : ", unapproved"}
                  {coverage.runbook.createdAt
                    ? ` · saved ${relativeTime(coverage.runbook.createdAt)}`
                    : ""}
                </>
              ) : (
                " · no runbook yet"
              )}
            </span>
          </div>

          {unavailable.length > 0 ? (
            <div
              className="sq-run-banner amber"
              role="status"
              data-testid="unavailable"
            >
              <span>
                <strong>
                  {unavailable.length} feature
                  {unavailable.length === 1 ? "" : "s"} not available on{" "}
                  {coverage.portalLabel || coverage.portal} yet.
                </strong>{" "}
                Each one names the operations that would enable it:
              </span>
              <ul className="sq-cov-needs">
                {unavailable.map((entry) => (
                  <li key={entry.feature}>
                    {unavailableLine(entry)}
                    {entry.status === "partial"
                      ? " (some are recorded already)"
                      : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="sq-run-banner green" role="status">
              <span>Every Atlas feature is available on this portal.</span>
            </div>
          )}

          {coverage.features.map((feature) => (
            <section
              key={feature.key}
              className="sq-cov-feature"
              data-feature={feature.key}
              data-status={feature.status}
            >
              <h3 className="sq-section-title">
                {feature.label}{" "}
                <span
                  className={`sq-status ${
                    feature.status === "complete"
                      ? "sq-status-submitted"
                      : feature.status === "partial"
                        ? "sq-cov-partial"
                        : "sq-status-failed"
                  }`}
                >
                  {FEATURE_STATUS_LABEL[feature.status] || feature.status}
                </span>
                {feature.gated && (
                  <span className="sq-muted sq-cov-gated">
                    {" "}
                    · gates{" "}
                    {feature.key === "submit"
                      ? "Submit to queue"
                      : feature.key === "corrections"
                        ? "corrected claims"
                        : "payment posting"}
                  </span>
                )}
              </h3>
              <p className="sq-muted">{feature.description}</p>
              <div className="sq-table-wrap">
                <table className="sq-table sq-cov-table">
                  <thead>
                    <tr>
                      <th>Operation</th>
                      <th>Kind</th>
                      <th>Needed</th>
                      <th>State</th>
                      <th>What it does / what it still needs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...feature.required, ...feature.uses].map((key) => {
                      const op = byKey[key];
                      if (!op) return null;
                      const required = feature.required.includes(key);
                      return (
                        <tr
                          key={key}
                          data-operation={key}
                          data-state={op.state}
                        >
                          <td>
                            <code>{key}</code>
                          </td>
                          <td>{op.kind}</td>
                          <td className="sq-nowrap">
                            {required ? "Required" : "Optional"}
                          </td>
                          <td>
                            <StateBadge state={op.state} />
                          </td>
                          <td>
                            {op.description}
                            {op.reasons.length > 0 &&
                              op.state !== "covered" && (
                                <ul className="sq-cov-reasons">
                                  {op.reasons.map((reason, i) => (
                                    <li key={i}>{reason}</li>
                                  ))}
                                </ul>
                              )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}

export const coverageIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <path d="M14 17.5l2.5 2.5L21 15" />
  </svg>
);
